import { QueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { AssociationType } from "@/shared/domain/association";
import { FileRecord, IngestionStatus } from "@/shared/domain/files";
import { TopicRecord, MessagePathRecord } from "@/shared/domain/topics";
import * as UseFileTopics from "@/shared/domain/topics/hooks/useFileTopics";
import { Datasource } from "@/shared/state/visualization";
import {
  DatasetSource,
  FileSource,
} from "@/shared/state/visualization/schema/v1";

import { EphemeralWorkspaceState } from "./EphemeralWorkspaceState";

export interface WorkspaceData {
  files: EphemeralWorkspaceState["files"];
  topics: EphemeralWorkspaceState["topics"];
  messagePaths: EphemeralWorkspaceState["messagePaths"];
}

export class WorkspaceDataService {
  private domainServices: ReturnType<typeof useDomainServices>;
  private queryClient: QueryClient;

  constructor(
    domainServices: ReturnType<typeof useDomainServices>,
    queryClient: QueryClient,
  ) {
    this.domainServices = domainServices;
    this.queryClient = queryClient;
  }

  async fetchWorkspaceData(
    datasources: Datasource[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const { files, datasets } = this.partitionDatasource(datasources);

    if (datasets.length > 1) {
      throw new Error(
        "Unable to visualize multiple datasets at once. Please select files from a single dataset or visualize each dataset separately.",
      );
    }

    // Fetch dataset data (or empty)
    const datasetData =
      datasets.length === 1
        ? await this.fetchDatasetWorkspaceData(
            datasets[0].value.datasetId,
            abortController,
          )
        : this.emptyWorkspaceData();

    // Get file IDs that aren't already in dataset
    const datasetFileIds = new Set(datasetData.files.map((f) => f.file_id));
    const additionalFileIds = files
      .map((f) => f.value.fileId)
      .filter((id) => !datasetFileIds.has(id));

    // Fetch additional files (or empty)
    const fileData =
      additionalFileIds.length > 0
        ? await this.fetchFileWorkspaceData(additionalFileIds, abortController)
        : this.emptyWorkspaceData();

    // Merge and return
    return this.mergeWorkspaceData(datasetData, fileData);
  }

  private emptyWorkspaceData(): WorkspaceData {
    return { files: [], topics: [], messagePaths: [] };
  }

  private mergeWorkspaceData(
    a: WorkspaceData,
    b: WorkspaceData,
  ): WorkspaceData {
    return {
      files: [...a.files, ...b.files].sort((x, y) =>
        x.relative_path.localeCompare(y.relative_path, undefined, {
          sensitivity: "base",
        }),
      ),
      topics: [...a.topics, ...b.topics],
      messagePaths: [...a.messagePaths, ...b.messagePaths],
    };
  }

  private extractMessagePaths(topics: TopicRecord[]): MessagePathRecord[] {
    return topics.flatMap((topic) => topic.message_paths);
  }

  private async fetchDatasetWorkspaceData(
    datasetId: string,
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const fileRecords = await this.domainServices.datasetService.listAllFiles(
      datasetId,
      {
        abortSignal: abortController.signal,
      },
    );

    const ingestedFileRecords = this.getIngestedFiles(fileRecords);

    const topicRecords = await this.fetchTopicsForFiles(
      ingestedFileRecords.map((fileRecord) => fileRecord.file_id),
    );

    return {
      files: ingestedFileRecords,
      topics: topicRecords,
      messagePaths: this.extractMessagePaths(topicRecords),
    };
  }

  private async fetchFileWorkspaceData(
    fileIds: string[],
    abortController: AbortController,
  ): Promise<WorkspaceData> {
    const [fileRecords, topicRecords] = await Promise.all([
      Promise.all(
        fileIds.map((fileId) =>
          this.domainServices.files.getFileRecord(fileId, {
            abortSignal: abortController.signal,
          }),
        ),
      ),
      this.fetchTopicsForFiles(fileIds),
    ]);

    return {
      files: fileRecords,
      topics: topicRecords,
      messagePaths: this.extractMessagePaths(topicRecords),
    };
  }

  private async fetchTopicsForFiles(fileIds: string[]): Promise<TopicRecord[]> {
    const topicRecordsPerFile = await Promise.all(
      fileIds.map((fileId) =>
        this.queryClient.ensureQueryData({
          queryKey: UseFileTopics.getQueryKey(fileId),
          queryFn: ({ signal }) =>
            this.domainServices.topics.getTopicsByAssociation(
              {
                association_id: fileId,
                association_type: AssociationType.File,
              },
              {
                abortSignal: signal,
              },
            ),
        }),
      ),
    );
    return topicRecordsPerFile.flat();
  }
  private getIngestedFiles(fileRecords: FileRecord[]): FileRecord[] {
    return fileRecords.filter(
      (fileRecord) =>
        fileRecord.ingestion_status !== IngestionStatus.NotIngested,
    );
  }

  private partitionDatasource(datasources: Datasource[]): {
    files: FileSource[];
    datasets: DatasetSource[];
  } {
    return datasources.reduce<{
      files: FileSource[];
      datasets: DatasetSource[];
    }>(
      (data, datasource) => {
        if (datasource.type === "file") {
          data.files.push(datasource);
        } else {
          data.datasets.push(datasource);
        }
        return data;
      },
      {
        files: [],
        datasets: [],
      },
    );
  }
}
